import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../services/api';

interface User {
  id: string;
  email: string;
  username: string;
  role: string;
  emailVerified: boolean;
  avatarUrl: string | null;
  referralCode: string;
  createdAt: string;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  register: (
    email: string,
    username: string,
    password: string,
    referralCode?: string
  ) => Promise<void>;
  logout: () => void;
  refreshAuth: () => Promise<void>;
  setUser: (user: User) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isLoading: true,
      isAuthenticated: false,

      login: async (email, password, rememberMe = false) => {
        const response = await api.post('/auth/login', {
          email,
          password,
          rememberMe,
        });

        const { user, tokens } = response.data.data;

        set({
          user,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      register: async (
        email,
        username,
        password,
        referralCode
      ) => {
        const response = await api.post('/auth/register', {
          email,
          username,
          password,
          referralCode,
        });

        const { user, tokens } = response.data.data;

        set({
          user,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      logout: () => {
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      refreshAuth: async () => {
        try {
          const { refreshToken } = get();

          if (!refreshToken) {
            set({
              isLoading: false,
            });
            return;
          }

          const response = await api.post('/auth/refresh', {
            refreshToken,
          });

          const { tokens } = response.data.data;

          set({
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
            isAuthenticated: true,
            isLoading: false,
          });

        } catch (error) {
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      setUser: (user) =>
        set({
          user,
        }),

      setTokens: (accessToken, refreshToken) =>
        set({
          accessToken,
          refreshToken,
        }),
    }),

    {
      name: 'peage-auth',

      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
    }
  )
);
